import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import SEO from "@/components/storefront/SEO";

const TERMS = [
  ["Basting", "Temporarily holding the quilt sandwich (top, batting, backing) together before quilting — with pins, spray, or long stitches."],
  ["Batting", "The insulating middle layer of a quilt. Cotton, polyester, wool, or blends — each drapes and lofts differently."],
  ["Bias", "The 45° diagonal of woven fabric. Stretchy — great for curved binding, risky for un-stabilized seams."],
  ["Binding", "The fabric strip that wraps and finishes the raw edges of a quilt. Usually cut 2.25–2.5\" wide."],
  ["Charm pack", "A bundle of 5\" × 5\" precut squares, typically 40–42 coordinated prints from one collection."],
  ["EPP (English Paper Piecing)", "Hand-piecing technique where fabric is wrapped around paper templates — hexies are the classic."],
  ["Fat quarter", "A quarter yard cut 18\" × 22\" instead of the skinny 9\" × 44\" strip. More usable real estate for patchwork."],
  ["Feed dogs", "The toothed metal strips under the presser foot that pull fabric through the machine. Dropped for free-motion quilting."],
  ["Fussy cutting", "Cutting a piece to feature a specific motif of the print — worth the fabric waste, every time."],
  ["HST (Half-square triangle)", "A square made of two right triangles — the most-used unit in quilting after the plain square."],
  ["Jelly roll", "A precut bundle of 2.5\" × WOF strips, usually 40 per roll."],
  ["Loft", "The thickness and puffiness of batting. Low loft = flat and modern, high loft = cozy and puffy."],
  ["Longarm quilting", "Quilting the sandwich on a dedicated frame machine with a long throat — often offered as a service by longarm quilters."],
  ["Notions", "The small tools and supplies of sewing: pins, clips, needles, seam rippers, marking tools, measuring tape."],
  ["Precuts", "Factory-cut coordinated fabric bundles — charm packs, jelly rolls, layer cakes, fat quarter bundles."],
  ["Sashing", "Strips of fabric separating quilt blocks — gives blocks room to breathe and grows the quilt fast."],
  ["Seam allowance", "The distance between stitch line and raw edge. Quilting standard: a scant ¼\"."],
  ["Selvage", "The tightly woven, often printed edge of yardage. Trim it off — it shrinks and pulls differently."],
  ["Stash", "Your personal fabric collection. It is never finished, and that's fine."],
  ["UFO", "UnFinished Object. Every maker has a drawer of them. No judgment here."],
  ["Walking foot", "A presser foot that feeds top and bottom layers evenly — essential for straight-line quilting and binding."],
  ["WOF (Width of fabric)", "Selvage-to-selvage width of yardage, nominally 42–44\", realistically 40–42\" usable."],
];

export default function Glossary() {
  const [q, setQ] = useState("");
  const filtered = TERMS.filter(([t, d]) => !q || t.toLowerCase().includes(q.toLowerCase()) || d.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="mx-auto max-w-[900px] px-6 py-14 md:py-20" data-testid="glossary-page">
      <SEO
        title="Quilting & Sewing Glossary"
        description="Quilting terms explained: fat quarter, WOF, seam allowance, basting, selvage, notions, HST, sashing, EPP and more — a friendly maker's glossary."
      />
      <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Maker resources</div>
      <h1 className="font-display text-4xl leading-tight md:text-[64px]">
        The maker's <span className="font-display-soft italic">glossary</span>
      </h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#3A2E33]">
        Every craft has its dialect. Here's quilting's — short, honest definitions with zero gatekeeping, from basting to WOF.
      </p>

      <div className="relative mt-8">
        <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#6B5158]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a term…"
          className="w-full rounded-full border border-[#E6D4CC] bg-white py-3 pl-11 pr-4 text-sm outline-none focus:border-[#9D5C6C]"
          data-testid="glossary-search"
        />
      </div>

      <dl className="mt-8 divide-y divide-[#E6D4CC] rounded-md border border-[#E6D4CC] bg-white">
        {filtered.map(([term, def]) => (
          <div key={term} className="px-5 py-4 hover:bg-[#FAF6F1]" data-testid={`term-${term.split(" ")[0].toLowerCase()}`}>
            <dt className="font-display text-lg text-[#6D2E4B]">{term}</dt>
            <dd className="mt-1 text-[14px] leading-relaxed text-[#3A2E33]">{def}</dd>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-[#6B5158]">No matching term — try another word or <Link to="/contact" className="underline">ask us</Link>, we love this stuff.</div>
        )}
      </dl>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <Link to="/guides/precut-sizes" className="inline-flex items-center gap-2 rounded-full bg-[#6D2E4B] px-6 py-3 text-sm text-white hover:bg-[#59253d]">
          Precut size guide <ArrowRight size={14} />
        </Link>
        <Link to="/guides/quilt-calculators" className="text-sm text-[#2B2127] underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#9D5C6C]">
          Quilt calculators
        </Link>
      </div>
    </div>
  );
}
